import { useMemo } from 'react';
import {
  useMembershipPlans,
  membershipPlansFromQueryData,
} from '@/hooks/pricebook/useMembershipPlans';
import { computeEstimatePricingPreview } from '@/lib/sales/estimate-pricing-preview';
import { useAllEquipment } from '@/hooks/equipment/useEquipment';
import {
  priceMembershipOptionForEquipment,
  type MembershipSaleEquipment,
} from '@/lib/membership/home-sale-pricing';
import type {
  MembershipBillingOption,
  MembershipCoverageRule,
} from '@/components/organisms/pricebook/support/membership-plan-shared';
import { billingIntervalForOption } from '@/components/organisms/pricebook/support/membership-plan-shared';
import { usePricebookItems } from '@/hooks/pricebook/usePricebookItems';
import {
  combineEstimateMembershipInfo,
  type EstimateMembershipInfo,
} from './useEstimateMembershipInfo';
import { planToEstimateMembershipInfo } from './useMembershipInfoWithInCart';

/**
 * NOT-605 — the "join and save today" upsell. For a customer who is NOT already
 * a member, compute what they would pay under the best active plan. When the
 * estimate author explicitly added an optional membership line, that exact
 * plan wins instead of catalog discovery.
 *
 * Reuses the same `computeEstimatePricingPreview` engine the estimate already
 * uses, called once per plan — so the offer number matches what pricing would
 * actually apply if the plan were added.
 */
export interface MembershipOffer {
  setupFee?: number;
  planId: string;
  planName: string;
  /** What the document costs today (no membership). */
  nonMemberGrandTotal: number;
  /** What it would cost as a member of this plan. */
  memberGrandTotal: number;
  /** nonMemberGrandTotal − memberGrandTotal (0 for benefit-only offers). */
  savings: number;
  /** The plan's own price (the cost of joining). */
  planPrice: number | null;
  planPriceInterval: string | null;
  planPriceIntervalValue?: number | null;
  billingOptionId?: string | null;
  billingOptionLabel?: string | null;
  billingOptions?: Array<{
    id: string;
    label: string;
    price: number;
    intervalValue: number;
    intervalUnit: string;
  }>;
  /** Authored visit cadence across the plan, normalized to one year. */
  visitsPerYear?: number;
  /** The comparison starts from benefits the customer already owns. */
  comparesExistingMembership?: boolean;
}

type PreviewInputs = Omit<Parameters<typeof computeEstimatePricingPreview>[0], 'membershipInfo'>;

/**
 * What this plan costs THIS home on its default billing option: the option's
 * base price plus the coverage rules' extra-unit price for every covered unit
 * beyond the included quantity. Null when the plan has no billing options, so
 * the caller falls back to the plan's own price.
 */
function homePriceAndCadenceForPlan(
  plan: {
    billingOptions?: MembershipBillingOption[] | null;
    coverageRules?: MembershipCoverageRule[] | null;
    priceAmount?: number | null;
    billingIntervalValue?: number | null;
    billingIntervalUnit?: string | null;
  },
  equipment: MembershipSaleEquipment[],
  billingOptionId?: string | null,
): {
  price: number | null;
  intervalValue: number | null;
  intervalUnit: string | null;
} {
  const options = plan.billingOptions ?? [];
  if (options.length === 0) {
    return {
      price: plan.priceAmount ?? null,
      intervalValue: plan.billingIntervalValue ?? null,
      intervalUnit: plan.billingIntervalUnit ?? null,
    };
  }
  // Without an office-chosen option this is the price the plan is offered at,
  // which is never a free option's.
  const offered = options.filter((candidate) => !candidate.isComplimentary);
  const option = billingOptionId
    ? options.find((candidate) => candidate.id === billingOptionId)
    : (offered.find((candidate) => candidate.isDefault) ?? offered[0]);
  if (!option) return { price: null, intervalValue: null, intervalUnit: null };
  const interval = billingIntervalForOption(option);
  return {
    price: priceMembershipOptionForEquipment({
      option,
      coverageRules: plan.coverageRules ?? [],
      equipment,
    }).priceAmount,
    intervalValue: interval.billingIntervalValue,
    intervalUnit: interval.billingIntervalUnit,
  };
}

function visitsPerYearForPlan(plan: {
  recurringServices?: Array<{
    recurrenceType?: string | null;
    recurrenceInterval?: number | null;
    recurrenceMonths?: number[] | null;
    durationType?: string | null;
    totalVisits?: number | null;
    firstVisitComplete?: boolean | null;
  }> | null;
  billingOptions?: MembershipBillingOption[] | null;
}): number {
  const termMonths = plan.billingOptions?.find((option) => option.isDefault)?.termMonths ?? 0;
  const visits = (plan.recurringServices ?? []).reduce((total, service) => {
    const interval = Math.max(1, Number(service.recurrenceInterval) || 1);
    const recurring =
      service.recurrenceType === 'DAILY'
        ? 365 / interval
        : service.recurrenceType === 'WEEKLY'
          ? 52 / interval
          : service.recurrenceType === 'SEASONAL'
            ? (service.recurrenceMonths?.length ?? 0) / interval
            : 12 / interval;
    if (service.durationType !== 'FIXED' || !service.totalVisits) {
      return total + recurring;
    }
    const remaining = Math.max(0, service.totalVisits - (service.firstVisitComplete ? 1 : 0));
    return (
      total + (termMonths > 0 ? remaining * (12 / termMonths) : Math.min(recurring, remaining))
    );
  }, 0);
  return Math.round(visits * 10) / 10;
}

function cheaperPlanPrice(candidate: number | null, current: number | null): boolean {
  if (candidate == null) return false;
  return current == null || candidate < current;
}

export function useMembershipOfferPreview(args: {
  inputs: PreviewInputs;
  /** Don't fetch/compute until the customer's membership state is known. */
  enabled?: boolean;
  /** Whose home this is, so the offer quotes THIS home's price. */
  customerId?: string | null;
  serviceAddressId?: string | null;
  /** Exact plan from the estimate's optional membership line. */
  planId?: string | null;
  /** Exact cadence/price option chosen on that same estimate line. */
  billingOptionId?: string | null;
  /** Active plans already owned by this customer; never recommend duplicates. */
  excludedPlanIds?: readonly string[];
  /** Active pricing benefits already applied to the estimate. */
  baselineMembershipInfo?: EstimateMembershipInfo | null;
}): MembershipOffer | null {
  const {
    inputs,
    enabled = true,
    customerId,
    serviceAddressId,
    planId,
    billingOptionId,
    excludedPlanIds = [],
    baselineMembershipInfo = null,
  } = args;
  // Property-level plans can be recommended as soon as the customer is known.
  // Coverage pricing additionally waits for the selected home's equipment
  // below, so a missing address can never be mistaken for a zero-unit home.
  const shouldFetch = enabled && Boolean(customerId);
  const shouldFetchEquipment = shouldFetch && Boolean(serviceAddressId);
  const { data: plans } = useMembershipPlans(
    { status: 'ACTIVE', limit: 100 },
    { enabled: shouldFetch, allPages: true },
  );
  // A plan needs a linked Pricebook row to be sold through the estimate
  // configurator. That includes legacy SERVICE-shaped membership rows; an
  // explicitly-authored line already has its source and skips this guard.
  const { data: membershipItems } = usePricebookItems(
    // Both modern MEMBERSHIP rows and legacy SERVICE-shaped memberships carry
    // this category. The server-side filter keeps this catalog lookup narrow.
    { taxCategory: 'membership', status: 'ACTIVE', limit: 100 },
    { enabled: shouldFetch && !planId, showErrorToast: false, allPages: true },
  );
  // NOT-628 — the same equipment the sale dialog prices against; shares its
  // query cache, so this costs nothing extra when both are on screen.
  const { data: equipmentData } = useAllEquipment(
    { customerId: customerId ?? undefined, serviceAddressId: serviceAddressId ?? undefined },
    shouldFetchEquipment,
  );
  const homeEquipment = useMemo(
    () =>
      (equipmentData?.items ?? [])
        // Same active-unit rule the sale dialog prices on, so the offer and the
        // dialog cannot quote two different homes.
        .filter((unit) => unit.status === 'ACTIVE' || unit.status === 'UNKNOWN')
        .map((unit) => ({
          id: unit.id,
          category: String(unit.category ?? ''),
          name: unit.name ?? null,
          serialNumber: unit.serialNumber ?? null,
        })),
    [equipmentData],
  );

  return useMemo(() => {
    // Canonical normalizer — the list API returns `{ data: [...] }`, so reading
    // only `.items` (the old inline code) always yielded [] and the offer never
    // rendered. membershipPlansFromQueryData handles both shapes.
    const planList = membershipPlansFromQueryData(plans);
    if (!shouldFetch || planList.length === 0 || (!planId && !membershipItems)) {
      return null;
    }

    const base = computeEstimatePricingPreview({
      ...inputs,
      membershipInfo: baselineMembershipInfo,
    });
    if (base.grandTotal <= 0) return null;

    const excluded = new Set(excludedPlanIds);
    const offerablePlanIds = new Set(
      (membershipItems?.items ?? []).flatMap((item) => {
        const ids = item.membershipPlanIds ?? [];
        const configuredId = item.membershipConfig?.planId;
        return configuredId ? [...ids, configuredId] : ids;
      }),
    );
    let best: MembershipOffer | null = null;
    for (const plan of planList) {
      if (
        !plan.id ||
        excluded.has(plan.id) ||
        (planId && plan.id !== planId) ||
        (!planId && !offerablePlanIds.has(plan.id))
      ) {
        continue;
      }
      // `undefined` means the selected home's equipment has not resolved (or
      // there is no selected home). Only coverage-scoped plans depend on that
      // context; property-level plans keep their authored scalar price.
      if ((plan.coverageRules?.length ?? 0) > 0 && !equipmentData) continue;
      const candidateMembershipInfo = planToEstimateMembershipInfo(plan);
      const preview = computeEstimatePricingPreview({
        ...inputs,
        membershipInfo: combineEstimateMembershipInfo([
          baselineMembershipInfo,
          candidateMembershipInfo,
        ]),
      });
      const savings = Math.round((base.grandTotal - preview.grandTotal) * 100) / 100;
      // Automatic discovery promises "save", so it requires real savings.
      // An explicitly-authored plan may still be a valid visits/waivers offer
      // even when today's estimate happens to save $0.
      if (savings < 0 || (!planId && savings === 0)) continue;
      const pricedPlan = homePriceAndCadenceForPlan(plan, homeEquipment, billingOptionId);
      if (
        !best ||
        savings > best.savings ||
        (savings === best.savings && cheaperPlanPrice(pricedPlan.price, best.planPrice))
      ) {
        best = {
          planId: plan.id,
          planName: plan.name,
          nonMemberGrandTotal: base.grandTotal,
          memberGrandTotal: preview.grandTotal,
          savings,
          // NOT-628 — a plan whose price scales with the home must be quoted
          // for THIS home. Showing the $19 base against a two-system house the
          // sale then charges $27 for made the offer a number we do not honour.
          planPrice: pricedPlan.price,
          setupFee: Number(plan.setupFee) || 0,
          planPriceInterval: pricedPlan.intervalUnit,
          planPriceIntervalValue: pricedPlan.intervalValue,
          visitsPerYear: visitsPerYearForPlan(plan),
          comparesExistingMembership: Boolean(baselineMembershipInfo),
        };
      }
    }
    return best;
    // computeEstimatePricingPreview is pure; plans + inputs are the only inputs.
  }, [
    shouldFetch,
    plans,
    membershipItems,
    inputs,
    homeEquipment,
    equipmentData,
    planId,
    billingOptionId,
    excludedPlanIds,
    baselineMembershipInfo,
  ]);
}
